import React from "react";
import { Link } from "react-router-dom";
import { getNews } from "../api/newsApi";

const LatestNews = () => {
  const [newsList, setNewsList] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");

  React.useEffect(() => {
    let isMounted = true;
    const fetchNews = async () => {
      try {
        setLoading(true);
        setError("");
        const response = await getNews();
        if (!response.success) return;
        if (isMounted) setNewsList(response.data.slice(0, 3)); // Get only latest 3 news items
      } catch (err) {
        if (isMounted) setError("Failed to load news. Please try again later.");
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    fetchNews();
    return () => {
      isMounted = false;
    };
  }, []);

  // Helper function to get a usable image URL (thumbnail for Drive links)
  const getImageUrl = (url) => {
    if (!url) return "/news/news1.png";

    // Google Drive link pattern
    const driveMatch = url.match(
      /https:\/\/drive\.google\.com\/file\/d\/([a-zA-Z0-9_-]+)\/?.*/
    );
    if (driveMatch && driveMatch[1]) {
      // Use Google Drive thumbnail
      return `https://drive.google.com/thumbnail?id=${driveMatch[1]}`;
    }

    return url;
  };

  return (
    <div className="w-full flex flex-col h-full">
      <h2 className="text-4xl font-bold text-yellow-400 mb-6">Latest News</h2>

      {loading && (
        <div className="text-center text-gray-400 py-8 animate-pulse">
          Loading news...
        </div>
      )}
      {error && !loading && (
        <div className="text-center text-red-400 py-8">{error}</div>
      )}
      {!loading && !error && newsList.length === 0 && (
        <div className="text-center text-gray-400 py-8">No news found.</div>
      )}

      {!loading && !error && newsList.length > 0 && (
        <div className="grid grid-cols-1 gap-6 flex-grow min-h-[380px]">
          {newsList.map((news, idx) => (
            <Link
              to="/news"
              key={idx}
              className="flex flex-col md:flex-row gap-4 group hover:bg-gray-800/30 p-4 rounded-lg transition-colors duration-300 md:h-32 h-auto"
            >
              <div className="w-full md:w-1/3 md:max-h-28">
                <div className="overflow-hidden rounded-xl h-52 md:h-28 relative shadow-lg shadow-blue-500/10">
                  <img
                    src={getImageUrl(news.imageUrl)}
                    alt={news.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    referrerPolicy="no-referrer"
                    onError={(e) => {
                      e.currentTarget.onerror = null;
                      e.currentTarget.src = "/news/news1.png";
                    }}
                  />
                </div>
              </div>

              <div className="w-full md:w-2/3 flex flex-col py-2 md:py-0">
                {/* <div className="text-gray-400 text-sm mb-1">{news.date ? news.date : "Recent News"}</div> */}
                <h3 className="text-white text-xl font-semibold mb-2">
                  {news.title}
                </h3>
                <p className="text-gray-300 line-clamp-3 md:line-clamp-2 text-sm">
                  {news.description}
                </p>
                <div className="mt-3 text-blue-400 group-hover:text-blue-300 transition-colors text-sm">
                  Read more →
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}

      <div className="mt-auto pt-6 text-center">
        <Link
          to="/news"
          onClick={() => window.scrollTo(0, 0)}
          className="group inline-flex items-center justify-center gap-2 px-8 py-3 bg-gradient-to-r from-blue-600 to-blue-500 text-white font-semibold rounded-full hover:from-amber-700 hover:to-amber-600 transform transition-all duration-300 hover:scale-105 shadow-lg hover:shadow-blue-500/30 border border-blue-500/20 min-w-[180px]"
        >
          <span>View All News</span>
          <svg
            className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M13 7l5 5m0 0l-5 5m5-5H6"
            />
          </svg>
        </Link>
      </div>
    </div>
  );
};

export default LatestNews;
